import React, { useState, useEffect } from 'react'
import OperationsSelect from './Select'

/**
 * This function returns an array containing all the keys of the tree
 * @param formattedTree {Array<interventionDataTypes}
 * @returns  {string[]}
 */

export const getKeys = (formattedTree: Array<interventionDataTypes>): string[] => {
    let keys: string[] = []
    formattedTree.forEach(intervention => {
        keys.push(intervention.key)
        intervention.children.forEach(section => {
            keys.push(section.key)
            section.children.forEach(run => {
                keys.push(run.key)
                run.children.forEach(operation => {
                    keys.push(operation.key)
                })
            })
        })
    })
    return keys
}

/**
 * This function summarizes the types of runs found on the tree into an array of objects
 * @param formattedTree {interventionDataTypes[]}
 * @returns {Array<{value: string, label: string | undefined}>}
 */

export const getRunTypes = (
    formattedTree: Array<interventionDataTypes>
): Array<{ label: string | undefined; value: string }> => {
    let runTypes: Array<{ value: string; label: string } | undefined> = []
    formattedTree.forEach(intervention => {
        intervention.children.forEach(section => {
            section.children.forEach(run => {
                runTypes.filter(e => e.value === run.type).length > 0
                    ? null
                    : runTypes.push({
                          value: run.type,
                          label: run.type
                      })
            })
        })
    })
    return runTypes.sort((a, b) => (a.value > b.value ? 1 : a.value < b.value ? -1 : 0))
}

/**
 * This function returns a string of all keys to be used as the defaults opened nodes of the tree
 *
 * @param formattedTree {interventionDataTypes[]} - the input tree of drilling operations
 * @returns {string[]}
 */

const determineDefaultExpandedKeys = (formattedTree: interventionDataTypes[]): string[] => {
    let res: string[] = []
    if (formattedTree.length > 0) {
        formattedTree.forEach((intervention, i) => {
            res.push(intervention.key)
            intervention.children.forEach((section, j) => {
                res.push(section.key)
                if (i === formattedTree?.length - 1 && j === intervention.children.length - 1)
                    res.push(section.children[section.children.length - 1].key)
            })
        })
    }
    return res
}

/**
 * This component sets all the keys for each line of the tree, and defines the nodes that will be expanded from the tree
 * @param formattedTree {Array<interventionDataTypes}- the operations tree formatted
 * @returns {JSX.Element} - a child that will return a JSX Element
 */

const TreeKeys = ({ formattedTree }: { formattedTree: Array<interventionDataTypes> }): JSX.Element => {
    const [allKeys, setAllKeys] = useState<string[]>(() => (formattedTree?.length > 0 ? getKeys(formattedTree) : []))

    useEffect(() => {
        if (formattedTree?.length > 0) {
            setAllKeys(getKeys(formattedTree))
        }
    }, [JSON.stringify(formattedTree)])

    const [defaultExpandedKeys, setDefaultExpandedKeys] = useState<string[]>(
        determineDefaultExpandedKeys(formattedTree)
    )

    useEffect(() => setDefaultExpandedKeys(determineDefaultExpandedKeys(formattedTree)), [
        JSON.stringify(formattedTree)
    ])

    return (
        <OperationsSelect
            tree={{
                formattedTree: formattedTree,
                allKeys: allKeys,
                defaultExpandedKeys: defaultExpandedKeys,
                runTypesList: getRunTypes(formattedTree)
            }}
        />
    )
}

export default TreeKeys
